import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface OpenTicket {
  id: string;
  subject: string;
  status: "new" | "open" | "pending" | "in_progress" | "closed";
  ticket_number?: string;
}

interface TicketTabsState {
  openTickets: OpenTicket[];
  activeTabId: string | null;
  lastViewPath: string; // En son bulunulan ANSICHT (liste) rotası — son sekme kapanınca buraya dönülür

  // Actions
  addTab: (ticket: OpenTicket) => void;
  removeTab: (ticketId: string) => void;
  setActiveTab: (ticketId: string | null) => void;
  updateTab: (ticketId: string, updates: Partial<OpenTicket>) => void;
  replaceTab: (ticket: OpenTicket, replaceId: string) => void; // Replace specific tab with new ticket
  clearAllTabs: () => void;
  setLastViewPath: (path: string) => void;
}

const MAX_TABS = 15;

// Validate ticket object
const isValidTicket = (ticket: any): ticket is OpenTicket => {
  return ticket && typeof ticket.id === 'string' && typeof ticket.subject === 'string';
};

const useTicketTabsStore = create<TicketTabsState>()(
  persist(
    (set, get) => ({
      openTickets: [],
      activeTabId: null,
      lastViewPath: '/tickets/all',

      setLastViewPath: (path: string) => {
        if (typeof path === 'string' && path) set({ lastViewPath: path });
      },

      addTab: (ticket: OpenTicket) => {
        if (!isValidTicket(ticket)) return;

        const { openTickets } = get();
        // Filter out any invalid tickets first
        const validTickets = openTickets.filter(isValidTicket);

        // Check if ticket already exists
        const existingTicket = validTickets.find(t => t.id === ticket.id);
        if (existingTicket) {
          // Just set as active
          set({ activeTabId: ticket.id, openTickets: validTickets });
          return;
        }

        // Enforce max tabs limit
        let newTickets = [...validTickets, ticket];
        if (newTickets.length > MAX_TABS) {
          // Remove oldest tab (first one)
          newTickets = newTickets.slice(1);
        }

        set({
          openTickets: newTickets,
          activeTabId: ticket.id,
        });
      },

      removeTab: (ticketId: string) => {
        const { openTickets, activeTabId } = get();
        const newTickets = openTickets.filter(t => t.id !== ticketId);

        let newActiveTabId = activeTabId;
        if (activeTabId === ticketId) {
          // If closing active tab, switch to another
          if (newTickets.length > 0) {
            newActiveTabId = newTickets[newTickets.length - 1].id;
          } else {
            newActiveTabId = null;
          }
        }

        set({
          openTickets: newTickets,
          activeTabId: newActiveTabId,
        });
      },

      setActiveTab: (ticketId: string | null) => {
        set({ activeTabId: ticketId });
      },

      updateTab: (ticketId: string, updates: Partial<OpenTicket>) => {
        const { openTickets } = get();
        const newTickets = openTickets.map(t =>
          t.id === ticketId ? { ...t, ...updates } : t
        );
        set({ openTickets: newTickets });
      },

      replaceTab: (ticket: OpenTicket, replaceId: string) => {
        if (!isValidTicket(ticket)) return;

        const { openTickets } = get();
        const validTickets = openTickets.filter(isValidTicket);

        // If ticket already exists in tabs, just set as active
        if (validTickets.find(t => t.id === ticket.id)) {
          set({ activeTabId: ticket.id, openTickets: validTickets });
          return;
        }

        // Find and replace the specified tab
        const tabToReplace = validTickets.find(t => t.id === replaceId);
        if (!tabToReplace) {
          // If tab to replace not found, just add as new tab
          let newTickets = [...validTickets, ticket];
          if (newTickets.length > MAX_TABS) {
            newTickets = newTickets.slice(1);
          }
          set({ openTickets: newTickets, activeTabId: ticket.id });
          return;
        }

        // Replace the tab
        const newTickets = validTickets.map(t =>
          t.id === replaceId ? ticket : t
        );

        set({
          openTickets: newTickets,
          activeTabId: ticket.id,
        });
      },

      clearAllTabs: () => {
        set({ openTickets: [], activeTabId: null });
      },
    }),
    {
      name: 'ticket-tabs-storage',
      partialize: (state) => ({
        openTickets: state.openTickets,
        activeTabId: state.activeTabId,
        lastViewPath: state.lastViewPath,
      }),
      onRehydrateStorage: () => (state) => {
        // Validate and clean up hydrated state
        if (state) {
          const validTickets = (state.openTickets || []).filter(isValidTicket);
          if (validTickets.length !== state.openTickets?.length) {
            state.openTickets = validTickets;
          }
          // Ensure activeTabId exists in openTickets
          if (state.activeTabId && !validTickets.find(t => t.id === state.activeTabId)) {
            state.activeTabId = validTickets.length > 0 ? validTickets[0].id : null;
          }
        }
      },
    }
  )
);

export default useTicketTabsStore;
